import React, { useState } from 'react';
import { PageLayout } from '@/shared/layout';
import { Button, KPICard, Modal, ActionToolbar, DataNavigator, StatusBadge } from '@/components/ui';
import { useAttendance } from './hooks/useAttendance';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useTeamsList } from '@/modules/teams/list/hooks/useTeamsList';
import { toast } from 'sonner';
import { AttendanceCard } from './components/AttendanceCard';
import { AttendanceCalendar } from './components/AttendanceCalendar';
import { Calendar, MapPin, Users, Clock, LogOut } from 'lucide-react';

export default function AttendancePage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const { projects = [] } = useProjects(companyId);
  const { teams = [] } = useTeamsList(companyId);

  const {
    records,
    stats,
    isLoading,
    filters,
    setFilters,
    checkIn,
    checkOut,
    isCheckingIn,
    isCheckingOut
  } = useAttendance(companyId);

  const handleCheckIn = async () => {
    if (!selectedProjectId) {
      toast.error('Vui lòng chọn dự án trước khi Check-in');
      return;
    }

    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      await checkIn({
        project_id: selectedProjectId,
        user_id: profile?.id,
        check_in_lat: pos.coords.latitude,
        check_in_lng: pos.coords.longitude,
        company_id: companyId
      });

      setShowCheckIn(false);
      toast.success('Check-in thành công! Chúc bạn một ngày làm việc hiệu quả.');
    } catch (error: any) {
      toast.error('Lỗi Check-in: ' + (error.code === 1 ? 'Vui lòng cho phép truy cập vị trí' : error.message));
    }
  };

  const handleCheckOut = async (id: string) => {
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      await checkOut({ id, lat: pos.coords.latitude, lng: pos.coords.longitude });
      toast.success('Đã Check-out thành công.');
    } catch (error: any) {
      toast.error('Lỗi Check-out: ' + error.message);
    }
  };

  return (
    <PageLayout
      title="Chấm công (Attendance)"
      description="Quản lý hiện diện, giờ công và tăng ca của nhân sự ngoài hiện trường."
      breadcrumbs={[{ label: 'Nhân sự' }, { label: 'Chấm công' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="rounded-2xl gap-2 font-bold text-xs"
            onClick={() => setShowCalendar(true)}
          >
            <Calendar className="w-4 h-4" />
            Xem lịch công
          </Button>
          <AuthButton
            module="maintenance"
            action="check_in"
            variant="primary"
            className="rounded-2xl gap-2 font-bold text-xs shadow-lg shadow-blue-500/20"
            onClick={() => setShowCheckIn(true)}
          >
            <MapPin className="w-4 h-4" />
            Check-in Hiện trường
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-8">
        {/* KPI Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard title="Hiện diện hôm nay" value={stats?.presentToday || 0} icon={Users} variant="success" />
          <KPICard title="Đi muộn" value={stats?.lateToday || 0} icon={Clock} variant="warning" />
          <KPICard title="Vắng mặt" value={stats?.onLeaveToday || 0} icon={LogOut} variant="danger" />
          <KPICard title="Tổng giờ công" value={(stats?.totalManHoursMonth || 0).toFixed(1)} icon="ri-history-line" variant="info" suffix="giờ" />
        </div>

        <ActionToolbar
          searchValue={filters.search}
          onSearchChange={(v) => setFilters({ ...filters, search: v })}
          searchPlaceholder="Tìm nhân viên, đội nhóm..."
          filterOptions={[
            {
              id: 'projectId', label: 'Dự án', type: 'select', choices: [
                { value: 'all', label: 'Tất cả dự án' },
                ...projects.map(p => ({ value: p.id, label: p.name }))
              ]
            },
            {
              id: 'teamId', label: 'Tổ đội', type: 'select', choices: [
                { value: 'all', label: 'Tất cả tổ đội' },
                ...teams.map(t => ({ value: t.id, label: t.name }))
              ]
            }
          ]}
          onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
          actions={
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              className="h-12 rounded-3xl border-none bg-slate-50/50 text-sm px-6 focus:ring-4 focus:ring-blue-500/10 transition-all font-black text-slate-600 cursor-pointer"
            />
          }
        />

        <DataNavigator
          isLoading={isLoading}
          data={records}
          columns={[
            {
              key: 'user',
              title: 'Nhân sự',
              render: (_, record) => (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold border-2 border-white shadow-sm">
                    {record.user?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div className="text-sm font-black text-slate-900">{record.user?.full_name}</div>
                    <div className="text-[10px] text-slate-400 font-bold  tracking-wider">{record.team?.name || 'Vãng lai'}</div>
                  </div>
                </div>
              )
            },
            {
              key: 'project',
              title: 'Dự án',
              render: (_, record) => (
                <div className="text-xs font-bold text-slate-600">{record.project?.name}</div>
              )
            },
            {
              key: 'check_in',
              title: 'Giờ vào',
              render: (val) => val ? (
                <div className="flex items-center gap-1.5 text-xs font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg w-fit">
                  <Clock className="w-3 h-3" />
                  {new Date(val).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                </div>
              ) : '-'
            },
            {
              key: 'check_out',
              title: 'Giờ ra',
              render: (val) => val ? (
                <div className="flex items-center gap-1.5 text-xs font-black text-slate-600 bg-slate-100 px-2 py-1 rounded-lg w-fit">
                  <LogOut className="w-3 h-3" />
                  {new Date(val).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                </div>
              ) : <StatusBadge status="active" label="Đang làm việc" variant="success" />
            }
          ]}
          mobileCardRender={(record) => (
            <AttendanceCard
              record={record}
              onCheckOut={handleCheckOut}
              isCheckingOut={isCheckingOut}
              currentUserId={profile?.id}
            />
          )}
          emptyTitle="Chưa có dữ liệu chấm công"
          emptyDescription={`Không tìm thấy bản ghi nào cho ngày ${new Date(filters.date).toLocaleDateString('vi-VN')}.`}
        />
      </div>

      {/* Check-in Modal */}
      <Modal
        isOpen={showCheckIn}
        onClose={() => setShowCheckIn(false)}
        title="Bắt đầu ca làm việc"
        size="sm"
      >
        <div className="space-y-8 py-4">
          <div className="p-6 bg-blue-50/50 rounded-[28px] border border-blue-100/50 flex items-center gap-5">
            <div className="w-16 h-16 bg-white rounded-[20px] shadow-sm flex items-center justify-center text-blue-600 border border-blue-50">
              <Calendar className="w-8 h-8" />
            </div>
            <div>
              <p className="text-[10px] text-blue-500 font-black  tracking-widest mb-1">Thời gian hiện tại</p>
              <p className="text-2xl font-black text-slate-900 leading-none">
                {new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-[11px] font-black text-slate-400  tracking-widest ml-1">Chọn dự án đang công tác *</label>
            <select
              required
              className="w-full h-14 rounded-2xl border-slate-100 bg-slate-50/50 focus:border-blue-500 focus:ring-0 text-sm font-bold px-4 transition-all appearance-none cursor-pointer"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              <option value="">-- Danh sách dự án --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50/50 border border-amber-100/50">
              <MapPin className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-700 font-bold leading-relaxed  tracking-wider">
                Hệ thống sẽ ghi lại tọa độ GPS của bạn để xác thực vị trí làm việc.
              </p>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button variant="ghost" className="flex-1 h-12 rounded-2xl font-bold" onClick={() => setShowCheckIn(false)}>Hủy bỏ</Button>
            <Button
              variant="primary"
              className="flex-1 h-12 rounded-2xl font-black  tracking-widest shadow-lg shadow-blue-500/20"
              onClick={handleCheckIn}
              isLoading={isCheckingIn}
            >
              Check-in Ngay
            </Button>
          </div>
        </div>
      </Modal>

      {/* Calendar Modal */}
      <Modal
        isOpen={showCalendar}
        onClose={() => setShowCalendar(false)}
        title="Lịch sử chấm công"
        size="lg"
      >
        <div className="py-2">
          <AttendanceCalendar />
          <div className="mt-6 flex justify-end">
            <Button variant="ghost" className="rounded-2xl font-bold" onClick={() => setShowCalendar(false)}>Đóng lại</Button>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
